import type { CanvasContent } from "./canvas-content";
import { schema } from "./schema";
export const LIBRARY_MAX_BYTES = 8_000_000;
export const LIBRARY_ACCOUNT_BYTES = 50_000_000;
export const LIBRARY_MAX_ITEMS = 50;
export type LibraryBundle = {
  version: 1;
  preview?: { image: string; width: number; height: number };
  content: CanvasContent;
  images: Record<string, string>;
};
export type LibraryItem = {
  id: string;
  name: string;
  bytes: number;
  created: number;
  archived: number;
};
const supported = new Set([
  "draw",
  "geo",
  "arrow",
  "line",
  "text",
  "note",
  "highlight",
  "group",
  "mark",
  "drafting",
  "image",
  "table",
  "document",
]);
export function validateLibraryBundle(value: unknown): LibraryBundle {
  if (!value || typeof value !== "object")
    throw new Error("Invalid library item.");
  const bundle = value as LibraryBundle,
    content = bundle.content;
  if (
    bundle.version !== 1 ||
    !content ||
    !content.schema ||
    !Array.isArray(content.shapes) ||
    content.shapes.length < 1 ||
    content.shapes.length > 100 ||
    !Array.isArray(content.assets) ||
    content.assets.length > 20 ||
    !Array.isArray(content.rootShapeIds) ||
    !bundle.images ||
    typeof bundle.images !== "object"
  )
    throw new Error("Save 1–100 objects and at most 20 images at a time.");
  if (
    new TextEncoder().encode(JSON.stringify(bundle)).length > LIBRARY_MAX_BYTES
  )
    throw new Error(
      "This library item exceeds 8 MB. Save a smaller selection.",
    );
  const ids = new Set<string>(content.shapes.map((s) => s.id));
  if (
    ids.size !== content.shapes.length ||
    !content.rootShapeIds.length ||
    content.rootShapeIds.some((id) => !ids.has(id))
  )
    throw new Error("Invalid library selection.");
  for (const shape of content.shapes) {
    if (!supported.has(shape.type))
      throw new Error(
        "Save drawing objects and images; select sheet contents instead of the sheet.",
      );
    schema.types.shape.validate(shape);
    if (shape.parentId.startsWith("shape:") && !ids.has(shape.parentId))
      throw new Error("Invalid library parent.");
    const seen = new Set<string>([shape.id]);
    let parent = shape.parentId;
    while (ids.has(parent as typeof shape.id)) {
      if (seen.has(parent)) throw new Error("Invalid library hierarchy.");
      seen.add(parent);
      parent = content.shapes.find((s) => s.id === parent)!.parentId;
    }
    if (
      shape.type === "image" &&
      (!shape.props.assetId ||
        !content.assets.some((a) => a.id === shape.props.assetId))
    )
      throw new Error("Image is not ready.");
  }
  if ((content.bindings?.length ?? 0) > 200)
    throw new Error("Too many bindings.");
  for (const binding of content.bindings ?? []) {
    schema.types.binding.validate(binding);
    if (!ids.has(binding.fromId) || !ids.has(binding.toId))
      throw new Error("Invalid library binding.");
  }
  for (const asset of content.assets) {
    schema.types.asset.validate(asset);
    if (asset.type !== "image" || asset.props.w * asset.props.h > 40_000_000)
      throw new Error("Use still images under 40 megapixels.");
    const image = bundle.images[asset.id];
    if (
      typeof image !== "string" ||
      !/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/.test(image)
    )
      throw new Error("Image bytes are missing or unsupported.");
  }
  if (
    Object.keys(bundle.images).some(
      (id) => !content.assets.some((a) => a.id === id),
    )
  )
    throw new Error("Unexpected image data.");
  if (bundle.preview !== undefined) {
    const p = bundle.preview;
    if (
      !p ||
      typeof p.image !== "string" ||
      p.image.length > 180_000 ||
      !/^data:image\/png;base64,iVBORw0KGgo[A-Za-z0-9+/]+=*$/.test(p.image) ||
      !Number.isFinite(p.width) ||
      !Number.isFinite(p.height) ||
      p.width <= 0 ||
      p.height <= 0 ||
      p.width > 1e7 ||
      p.height > 1e7
    )
      throw new Error("Invalid library preview.");
  }
  return bundle;
}
export function libraryName(value: unknown) {
  if (typeof value !== "string" || !value.trim() || value.trim().length > 100)
    throw new Error("Enter a name of 1–100 characters.");
  return value.trim();
}

export type LibraryPreview = {
  image?: string;
  width?: number;
  height?: number;
  count: number;
  kinds: string[];
};
/** No original image payloads or project links leave the preview endpoint. */
export function libraryPreview(bundle: LibraryBundle): LibraryPreview {
  return {
    image: bundle.preview?.image,
    width: bundle.preview?.width,
    height: bundle.preview?.height,
    count: bundle.content.shapes.filter((s) => s.type !== "group").length,
    kinds: [
      ...new Set(
        bundle.content.shapes
          .filter((s) => s.type !== "group")
          .map((s) =>
            s.type === "image"
              ? "Images"
              : ["text", "note", "table", "document"].includes(s.type)
                ? "Text & tables"
                : "Drawings",
          ),
      ),
    ],
  };
}
